import { Component,
  EventEmitter,
  Input,
  Output,
  ChangeDetectorRef,
  ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { FormViewService } from '../form-view.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GtwSnackbarService } from '../../../../shared/_services/gtw-snackbar.service';
import { ConfirmationModalConfig } from '../../../../shared/components/confirmation-modal/confirmation-modal.component';
import * as _ from 'lodash-es';

@Component({
    selector: 'gtw-bulk-export',
    templateUrl: './bulk-export.component.html',
    styleUrls: ['./bulk-export.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class BulkExportComponent {
  private readonly destroyRef = inject(DestroyRef);

  @Input() visible = false;
  @Input() entityTreeData: any[] = [];
  @Input() taxYear: any = {};
  @Input() scenario: any = {};
  @Input() jcdKey = 250;
  @Input() contSrc = 'FED';
  @Input() currentTheme = 'theme-black';
  @Input() pdfEngineUrl = '';
  @Input() bulkPdfUrl = '';
  @Input() enableReviewProcess = false;
  @Input() USER_SETTINGS: any = {};

  @Output() closed = new EventEmitter<void>();
  @Output() openDiagnostics = new EventEmitter<any>();

  /**
   * Legacy `ng-really-*` on first PDF Export button (bulk-export-modal.html
   * ~L721): confirm before `checkForDiagnostics()`; No → `bulkExport()`.
   */
  showClientReviewExportConfirm = false;
  readonly clientReviewExportConfirmConfig: ConfirmationModalConfig = {
    title: 'Bulk Export',
    message: 'Are you exporting the files for client review',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
  };

  PDFformats = [
    { format: 'PDF', type: 'PDF_FORM', name: 'Generate PDFs by form', selected: true },
    { format: 'PDF', type: 'PDF_ENTITY', name: 'Generate PDFs by Entity', selected: false },
    { format: 'PDF', type: 'PDF_SINGLE', name: 'Merge all PDFs into One PDF', selected: false }
  ];
  XMLformats = [
    { format: 'XML', type: 'XML_ENTITY', name: 'Generate XML by Entity', selected: false }
  ];
  ZIPformats = [
    { format: 'ZIP', type: 'ZIP_ENTITY', name: 'Generate ZIP by Entity', selected: false }
  ];

  groupList: any[] = [];
  selectedGroup: any = null;
  entityTree: any[] = [];
  entityTreeCopy: any[] = [];
  entityGroupTreeCopy: any[] = [];
  returnType = '';

  forms: any[] = [];
  Allforms: any[] = [];
  formLoading = false;
  formSearchTerm = '';
  downloadAllForms = false;
  noFormsMsg = '';
  foreignFormsDisplay = true;

  exportDisable = true;
  exportLoading = false;
  submissionIdsStr = '';

  searchTerm = '';
  expandAll = true;
  expandAllTxt = 'Collapse All';

  constructor(
    private cdr: ChangeDetectorRef,
    private formViewService: FormViewService,
    private gtwSnackbarService: GtwSnackbarService
  ) {}

  ngOnChanges(): void {
    if (this.visible && this.entityTreeData?.length > 0) {
      this.initModal();
    }
  }

  private initModal(): void {
    const tree = _.cloneDeep(this.entityTreeData);
    this.entityTreeCopy = _.cloneDeep(tree);

    this.groupList = tree.filter((item: any) => {
      if (this.contSrc === 'STATE') {
        return item.lvl === 0;
      } else {
        return (item.name !== 'Corporation') &&
          ((item.is_entity === 0 && item.consol_group_key === null && item.lvl <= 1) ||
           (item.is_entity === 0 && item.consol_group_key !== null && item.lvl === 1));
      }
    });

    this.selectedGroup = this.groupList[0];
    if (this.selectedGroup) {
      this.filterTreeByGroup(this.selectedGroup);
    }
    this.loadForms();
    this.cdr.detectChanges();
  }

  filterTreeByGroup(group: any): void {
    this.selectedGroup = group;
    const tree = this.entityTreeCopy.filter((item: any) => item.path?.includes(group.name));
    this.entityGroupTreeCopy = _.cloneDeep(tree);
    const entityItem = tree.find((i: any) => i.is_entity === 1);
    this.returnType = entityItem?.return_type || '';
    this.entityTree = tree;
    this.cdr.detectChanges();
  }

  onGroupChange(group: any): void {
    this.filterTreeByGroup(group);
    this.loadForms();
  }

  loadForms(): void {
    this.formLoading = true;
    this.cdr.detectChanges();

    this.formViewService.getBulkExportForms(
      this.pdfEngineUrl,
      this.taxYear?.value || this.taxYear?.label || '',
      this.returnType
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.formLoading = false;
        this.noFormsMsg = '';
        if (response.callSuccess === '1' && response.viewList) {
          this.Allforms = response.viewList;

          this.Allforms.forEach((form: any) => {
            if (form.is_whitepaper) {
              form.label = form.irs_form_no + '-Sch_' + form.schedule + '-' + form.line_no + ' ' + form.form;
            } else {
              form.label = form.form_sub && form.form_sub_cat === 'DIFF_PDF'
                ? ((form.form?.toLowerCase().includes('schedule') ? form.form.replace('Schedule', '-') : form.form) + '_' + form.form_sub)
                : (form.form?.toLowerCase().includes('schedule') ? form.form.replace('Schedule', '-') : form.form);
              if (form.form_sub_cat === 'SAME_PDF') form.form_sub = null;
            }
            form.hidden = form.lvl > 1;
            form.childrenHidden = form.lvl === 1;
            if (form.path?.includes('Corporate Domestic')) {
              form.type = 'D';
            } else if (form.path?.includes('Corporate Foreign')) {
              form.type = 'F';
            }
          });

          this.Allforms = _.uniqBy(this.Allforms, 'label');
          this.forms = _.cloneDeep(this.Allforms);
          this.foreignFormsDisplay = this.Allforms.some((f: any) => f.type === 'F');
          if (this.Allforms.length === 0) {
            this.noFormsMsg = 'No Forms Available';
          }
        } else {
          this.noFormsMsg = 'No Forms Available';
        }
        this.cdr.detectChanges();
      },
      () => {
        this.formLoading = false;
        this.noFormsMsg = 'PDFs not available at this time. The Global Tax support team has been notified.';
        this.cdr.detectChanges();
      }
    );
  }

  selectFormat(selectedFormat: any, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    [...this.PDFformats, ...this.XMLformats, ...this.ZIPformats].forEach(f => {
      f.selected = f.type === selectedFormat.type;
    });
    this.enableDisableExport();
    this.cdr.detectChanges();
  }

  selectEntity(item: any, event: Event): void {
    event.preventDefault();
    item.selected = !item.selected;

    if (!item.is_leaf) {
      const index = this.entityTree.indexOf(item);
      for (let i = index + 1; i < this.entityTree.length; i++) {
        if (this.entityTree[i].lvl <= item.lvl) break;
        this.entityTree[i].selected = item.selected;
      }
    }

    this.enableDisableExport();

    let submissionIds: string;
    const subId = (row: any) => row?.submission_id ?? row?.SUBMISSION_ID;
    if (this.selectedGroup?.consol_group_key === '2' || this.selectedGroup?.name === '3') {
      submissionIds = this.entityTree
        .filter((i: any) => i.selected && i.is_leaf && i.is_accepted !== 'Y')
        .map((i: any) => subId(i))
        .filter((id: any) => id != null && String(id).trim() !== '')
        .join(',');
    } else {
      const first = this.entityTree.find((i: any) => i.selected && i.is_leaf && i.is_accepted !== 'Y');
      submissionIds = first != null ? String(subId(first) ?? '').trim() : '';
    }
    this.submissionIdsStr = submissionIds;
    this.cdr.detectChanges();
  }

  selectForm(item: any, event: Event): void {
    event.preventDefault();
    item.selected = !item.selected;
    if (!item.is_leaf) {
      const index = this.forms.indexOf(item);
      for (let i = index + 1; i < this.forms.length; i++) {
        if (this.forms[i].lvl <= item.lvl) break;
        this.forms[i].selected = item.selected;
      }
    }
    this.cdr.detectChanges();
  }

  selectAllForm(): void {
    this.forms.forEach(f => f.selected = this.downloadAllForms);
    this.cdr.detectChanges();
  }

  searchForm(): void {
    if (!this.formSearchTerm || this.formSearchTerm.length === 0) {
      this.forms = _.cloneDeep(this.Allforms);
      this.cdr.detectChanges();
      return;
    }

    const results = new Set<any>();
    const parentNodes = this.Allforms.filter((item: any) => !item.is_leaf);

    for (let i = 0; i < this.Allforms.length; i++) {
      const node = this.Allforms[i];
      if (node.form?.toLowerCase().includes(this.formSearchTerm.toLowerCase())) {
        this.addParentNodes(results, parentNodes, node.path, 'form');
        node.hidden = false;
        results.add(node);
        if (!node.is_leaf) {
          while (i + 1 < this.Allforms.length && this.Allforms[i + 1].lvl > node.lvl) {
            i++;
            this.Allforms[i].hidden = false;
            results.add(this.Allforms[i]);
          }
        }
      }
    }
    this.forms = Array.from(results);
    this.cdr.detectChanges();
  }

  onPdfExportClick(): void {
    if (this.exportDisable || this.exportLoading) {
      return;
    }
    if (this.enableReviewProcess && this.submissionIdsStr !== '') {
      this.showClientReviewExportConfirm = true;
      this.cdr.markForCheck();
    } else {
      this.bulkExport();
    }
  }

  onClientReviewExportConfirmed(): void {
    this.showClientReviewExportConfirm = false;
    this.checkForDiagnostics();
    this.cdr.markForCheck();
  }

  onClientReviewExportCancelled(): void {
    this.showClientReviewExportConfirm = false;
    this.bulkExport();
    this.cdr.markForCheck();
  }

  checkForDiagnostics(): void {
    this.openDiagnostics.emit({
      submission_id: this.submissionIdsStr,
      client_key: this.USER_SETTINGS?.user?.client_key,
      tax_year: this.taxYear?.label,
      scenario: this.scenario?.scenario,
      jcd_key: this.jcdKey,
      onComplete: (ignored: boolean) => {
        this.bulkExport();
        this.createBulkSnapshot(this.submissionIdsStr, ignored);
      }
    });
  }

  bulkExport(): void {
    const selectedEntities = _.cloneDeep(this.entityTree.filter((i: any) => i.selected && i.is_leaf));
    const format: string[] = [];
    const mode: string[] = [];

    const selectedPDF = this.PDFformats.find(i => i.selected);
    if (selectedPDF) { mode.push(selectedPDF.type); format.push(selectedPDF.format); }
    const selectedXML = this.XMLformats.find(i => i.selected);
    if (selectedXML) { mode.push(selectedXML.type); format.push(selectedXML.format); }
    const selectedZIP = this.ZIPformats.find(i => i.selected);
    if (selectedZIP) { mode.push(selectedZIP.type); format.push(selectedZIP.format); }

    const selectedForms = this.downloadAllForms || format[0] !== 'PDF'
      ? [] : this.forms.filter((i: any) => i.selected && i.is_leaf);

    const formsPayload = selectedForms.map((form: any) => {
      const formSub = form.form_sub
        ? (this.selectedGroup?.consol_group_key === '2' ? form.form_sub.split('_')[0] : form.form_sub)
        : '';
      return {
        form_sub: formSub,
        irs_form: form.subform && form.form_sub_cat === 'DIFF_PDF' ? form.form + '_' + formSub : form.form,
        form_sub_cat: form.form_sub_cat,
        wpirs_form_no: form.is_whitepaper
          ? (form.irs_form_no?.toLowerCase().startsWith('f') ? form.irs_form_no.substring(1)
            : (form.irs_form_no?.toLowerCase().startsWith('irs') ? form.irs_form_no.substring(3) : form.irs_form_no))
          : '',
        schedule: form.schedule,
        line: form.line_no,
        part_no: form.part_no || 'NA',
        is_whitepaper: form.is_whitepaper
      };
    });

    const payload = { entities: selectedEntities, forms: formsPayload };
    this.exportLoading = true;
    this.cdr.detectChanges();

    this.formViewService.pdfsBulkExport(
      this.bulkPdfUrl, this.taxYear?.value || this.taxYear?.label || '',
      this.scenario?.scenario || '', this.jcdKey,
      format.join(','), mode.join(','), this.contSrc, payload
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.exportLoading = false;
        if (response.callSuccess === '1') {
          this.gtwSnackbarService.openSnackBar(
            'Bulk export request initiated successfully! Please go to PDF Bulk Export Screen to download the export when ready.',
            'gtw-snackbar--green'
          );
        } else {
          this.gtwSnackbarService.openSnackBar('Something went wrong!', 'gtw-snackbar--red');
        }
        this.cdr.detectChanges();
      },
      () => {
        this.exportLoading = false;
        this.gtwSnackbarService.openSnackBar('Something went wrong!', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  private createBulkSnapshot(submissionIds: string, ignored: boolean): void {
    const ids = submissionIds.split(',');
    ids.forEach(id => {
      this.formViewService.createSnapshot(this.bulkPdfUrl, id, this.taxYear?.label || '', this.scenario?.scenario || '', this.jcdKey, ignored).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    });
  }

  private enableDisableExport(): void {
    const selectedEntities = this.entityTree.filter((i: any) => i.selected && i.is_leaf);
    const anyFormatSelected = [...this.PDFformats, ...this.XMLformats, ...this.ZIPformats].some(f => f.selected);
    this.exportDisable = selectedEntities.length === 0 || !anyFormatSelected;
  }

  // Tree utilities
  toggleNode(item: any): void {
    if (item.is_leaf === 1) return;
    const tree = this.entityTree;
    const index = tree.indexOf(item) + 1;

    if (!item.childrenHidden) {
      item.childrenHidden = true;
      for (let i = index; i < tree.length; i++) {
        if (tree[i].lvl <= item.lvl) return;
        tree[i].hidden = true;
        if (i > 0 && tree[i - 1].lvl < tree[i].lvl) tree[i - 1].childrenHidden = true;
      }
    } else {
      item.childrenHidden = false;
      for (let i = index; i < tree.length; i++) {
        if (tree[i].lvl <= item.lvl) return;
        tree[i].hidden = tree[i].lvl - 1 !== item.lvl;
      }
    }
    this.cdr.detectChanges();
  }

  toggleFormNode(item: any): void {
    if (item.is_leaf === 1) return;
    const tree = this.forms;
    const index = tree.indexOf(item) + 1;

    if (!item.childrenHidden) {
      item.childrenHidden = true;
      for (let i = index; i < tree.length; i++) {
        if (tree[i].lvl <= item.lvl) return;
        tree[i].hidden = true;
      }
    } else {
      item.childrenHidden = false;
      for (let i = index; i < tree.length; i++) {
        if (tree[i].lvl <= item.lvl) return;
        tree[i].hidden = tree[i].lvl - 1 === item.lvl ? false : true;
      }
    }
    this.cdr.detectChanges();
  }

  visibleNodes(node: any): boolean { return !node.hidden; }

  expandCollapseAll(): void {
    this.expandAll = !this.expandAll;
    this.expandAllTxt = this.expandAll ? 'Collapse All' : 'Expand All';
    this.entityTree.forEach(row => {
      row.childrenHidden = !this.expandAll;
      if (!this.expandAll && row.lvl !== 0) row.hidden = true;
      if (this.expandAll) row.hidden = false;
    });
    this.cdr.detectChanges();
  }

  filterTreeData(): void {
    if (!this.searchTerm || this.searchTerm.length === 0) {
      this.entityTree = _.cloneDeep(this.entityGroupTreeCopy);
      this.entityTree.forEach(n => { n.hidden = false; n.childrenHidden = false; });
      this.cdr.detectChanges();
      return;
    }

    const results = new Set<any>();
    const parentNodes = this.entityGroupTreeCopy.filter((item: any) => !item.is_leaf);

    for (let i = 0; i < this.entityGroupTreeCopy.length; i++) {
      const node = this.entityGroupTreeCopy[i];
      node.hidden = false;
      node.childrenHidden = false;
      const str = ((node.name || '') + (node.leid || '') + (node.cdr_no || '') + (node.reporting_period || '')).toLowerCase();
      if (str.includes(this.searchTerm.toLowerCase())) {
        this.addParentNodes(results, parentNodes, node.path, 'name');
        results.add(node);
        if (!node.is_leaf) {
          while (i + 1 < this.entityGroupTreeCopy.length && this.entityGroupTreeCopy[i + 1].lvl > node.lvl) {
            i++;
            results.add(this.entityGroupTreeCopy[i]);
          }
        }
      }
    }
    this.entityTree = Array.from(results);
    this.cdr.detectChanges();
  }

  private addParentNodes(set: Set<any>, parentNodes: any[], path: string, labelKey: string): void {
    const nodes = (path || '').split('/');
    if (nodes.length > 2) {
      nodes.slice(1, -1).forEach(n => {
        const parent = parentNodes.find(item => item[labelKey]?.includes(n.trim()));
        if (parent) set.add(parent);
      });
    }
  }

  close(): void {
    this.showClientReviewExportConfirm = false;
    this.closed.emit();
  }

  getIconClass(item: any): string {
    if (item.is_leaf && item.is_entity) {
      if (item.is_accepted === 'Y') return 'fa fa-check toggle';
      if (item.is_locked === 'Y') return 'fa fa-lock toggle';
      if (item.external_y_n === 'Y') return 'fa fa-globe toggle';
      return 'fa fa-file-pdf-o toggle';
    }
    return item.childrenHidden ? 'fa fa-folder' : 'fa fa-folder-open';
  }

  getRecordsCount(type: 'forms' | 'entities', parent: string): number {
    const source = type === 'forms' ? this.forms : this.entityTree;
    return source.filter((item: any) => item.parent === parent).length;
  }
}
